import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  Calendar,
  ChevronRight,
  MapPin,
  Plus,
  X,
} from 'lucide-react';
import { CustomDropdown } from '../ui/CustomDropdown';
import { AddressSearch, type SelectedPlace } from '../ui/AddressSearch';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { DateTimePicker } from '../ui/DateTimePicker';
import {
  Quote,
  QuoteType,
  DiscoverySource,
  InboundChannel,
  DISCOVERY_SOURCE_LABEL,
  INBOUND_CHANNEL_LABEL,
  LEAD_STATUS_LABEL,
} from '../../types/admin';
import { useDuplicateLeadCheck, type DuplicateMatch } from '../../hooks/useDuplicateLeadCheck';
import { useOrgPricingGaps } from '../../hooks/useOrgPricingGaps';
import { MissingPricingModal } from './MissingPricingModal';
import { useUserProfile } from '../../context/UserProfileContext';
import { api } from '../../lib/api';

// ── Helpers ──────────────────────────────────────────────────────────────────

function getDefaultFollowUp(): string {
  const d = new Date();
  d.setDate(d.getDate() + 3);
  return d.toISOString().slice(0, 10);
}

function getTodayStr(): string {
  return new Date().toISOString().slice(0, 10);
}

function formatDateDisplay(value: string): string | null {
  if (!value) return null;
  const [y, m, d] = value.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

function formatCreatedDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric',
    });
  } catch {
    return '';
  }
}

// ── Formatting helpers for transit display ───────────────────────────────────

function formatDistance(meters: number): string {
  const miles = meters / 1609.34;
  return miles < 1 ? '< 1 mi' : `${miles.toFixed(1)} mi`;
}

function formatDuration(seconds: number): string {
  const mins = Math.round(seconds / 60);
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m > 0 ? `${h} hr ${m} min` : `${h} hr`;
}

// ── Dropdown options ─────────────────────────────────────────────────────────

const ALL_DISCOVERY_SOURCES: DiscoverySource[] = [
  'google', 'yelp', 'homeadvisor_angi', 'thumbtack', 'nextdoor',
  'social_media', 'repeat_customer', 'referral', 'trade_show_event', 'other',
];

const DISCOVERY_SOURCE_OPTIONS = [
  { value: '' as string, label: 'Select a source...' },
  ...ALL_DISCOVERY_SOURCES.map(s => ({ value: s as string, label: DISCOVERY_SOURCE_LABEL[s] })),
];

const ALL_INBOUND_CHANNELS: InboundChannel[] = [
  'manual_entry', 'booking_form', 'website_form', 'phone_call_inbound',
  'walk_in', 'partner_broker',
];

const INBOUND_CHANNEL_OPTIONS = [
  { value: '' as string, label: 'Select a channel...' },
  ...ALL_INBOUND_CHANNELS.map(c => ({ value: c as string, label: INBOUND_CHANNEL_LABEL[c] })),
];

const MOVE_TYPE_OPTIONS = [
  { value: '' as string, label: 'Select...' },
  { value: 'local', label: 'Local' },
  { value: 'intrastate', label: 'Long Distance (In-State)' },
  { value: 'interstate', label: 'Out of State' },
];

const BEDROOM_OPTIONS = [
  { value: '', label: 'Select...' },
  { value: '0', label: 'Studio / 0 Bedrooms' },
  { value: '1', label: '1 Bedroom' },
  { value: '2', label: '2 Bedrooms' },
  { value: '3', label: '3 Bedrooms' },
  { value: '4', label: '4 Bedrooms' },
  { value: '5', label: '5+ Bedrooms' },
];

// ── Types ────────────────────────────────────────────────────────────────────

export interface NewMoveFormData {
  customerCompanyName: string;
  pointOfContact?: { name?: string; phone?: string; email?: string };
  discoverySource?: DiscoverySource;
  inboundChannel?: InboundChannel;
  type?: QuoteType;
  nextFollowUp?: string;
  movingDate?: { preferredDate?: string; deadline?: string };
  estimatedMoveDateRange?: string;
  /** ISO timestamp (date or date-time) for the on-site estimate appointment. */
  estimateDate?: string;
  originZipCode?: string;
  destinationZipCode?: string;
  numberOfBedrooms?: number;
  squareFootage?: number;
  numberOfEmployees?: number;
  moveType?: 'local' | 'intrastate' | 'interstate';
  notes?: string;
  /** 'lead' | 'quote' — computed by the form based on feature flag + estimate date. */
  lifecycleStage: 'lead' | 'quote';
}

interface NewMoveFormProps {
  onClose: () => void;
  /** Create the record. The form decides whether the payload should go in as a lead or a quote. */
  onCreate: (data: NewMoveFormData) => Promise<Quote>;
  showTypeSelector?: boolean;
  defaultType?: QuoteType;
  orgId?: string | null;
}

// ── Shared styles ────────────────────────────────────────────────────────────

const LABEL_CLS = 'block text-[11px] font-bold uppercase tracking-widest text-slate';
const INPUT_CLS = `w-full h-11 px-3 rounded-lg border border-stone bg-white
  text-small text-midnight placeholder:text-slate
  focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
  transition-colors duration-150`;

// ── Duplicate Match Banner ──────────────────────────────────────────────────

function DuplicateBanner({ matches, onView }: {
  matches: DuplicateMatch[];
  onView: (matches: DuplicateMatch[]) => void;
}) {
  if (matches.length === 0) return null;

  const displayName = matches[0].pointOfContact?.name || matches[0].customerCompanyName;
  const createdStr = formatCreatedDate(matches[0].createdAt);

  return (
    <button
      type="button"
      onClick={() => onView(matches)}
      className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200
                 text-left text-[13px] text-amber-800 hover:bg-amber-100 transition-colors"
    >
      <AlertCircle size={14} className="shrink-0 text-amber-600" />
      <span className="flex-1 min-w-0 truncate">
        {matches.length === 1
          ? `Possible match: ${displayName}${createdStr ? ` \u2014 lead created ${createdStr}` : ''}`
          : `${matches.length} possible matches found`
        }
      </span>
      <span className="shrink-0 text-[12px] font-semibold text-amber-700 flex items-center gap-0.5">
        View <ChevronRight size={12} />
      </span>
    </button>
  );
}

// ── Existing Lead Comparison Sheet ──────────────────────────────────────────

function ExistingLeadSheet({ matches, onClose, onOpenLead }: {
  matches: DuplicateMatch[];
  onClose: () => void;
  onOpenLead: (id: string) => void;
}) {
  return (
    <div className="fixed inset-0 z-[60] flex flex-col justify-end md:items-end md:justify-stretch">
      <div className="absolute inset-0 bg-midnight/30" onClick={onClose} aria-hidden="true" />
      {/* Mobile: bottom sheet at ~60-65%. Desktop: side sheet. */}
      <div className="relative bg-white rounded-t-2xl max-h-[65vh] overflow-y-auto
                      md:rounded-t-none md:rounded-l-2xl md:max-h-full md:h-full md:w-[420px] md:max-w-[90vw]">
        <div className="md:hidden w-10 h-1 bg-stone rounded-full mx-auto mt-3" />

        <div className="sticky top-0 bg-white px-4 pt-4 pb-3 border-b border-stone z-10">
          <div className="flex items-center justify-between">
            <h3 className="font-display text-[17px] font-bold text-midnight">
              {matches.length === 1 ? 'Possible Match' : 'Possible Matches'}
            </h3>
            <button
              type="button"
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-slate
                         hover:text-midnight hover:bg-mist transition-colors"
              aria-label="Close"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        <div className="px-4 py-3 flex flex-col gap-3">
          {matches.map(m => (
            <div key={m.id} className="rounded-xl border border-stone p-4 flex flex-col gap-2.5">
              {/* Name / Company */}
              <div>
                <p className={LABEL_CLS}>Customer Name</p>
                <p className="text-small text-midnight mt-0.5">
                  {m.pointOfContact?.name || '\u2014'}
                </p>
              </div>
              {m.customerCompanyName && (
                <div>
                  <p className={LABEL_CLS}>Company</p>
                  <p className="text-small text-midnight mt-0.5">{m.customerCompanyName}</p>
                </div>
              )}

              {/* Phone */}
              <div>
                <p className={LABEL_CLS}>Phone</p>
                <p className="text-small text-midnight mt-0.5">
                  {m.pointOfContact?.phone || '\u2014'}
                </p>
              </div>

              {/* Email */}
              <div>
                <p className={LABEL_CLS}>Email</p>
                <p className="text-small text-midnight mt-0.5">
                  {m.pointOfContact?.email || '\u2014'}
                </p>
              </div>

              {/* Status + Created date */}
              <div className="flex items-center gap-3">
                {m.leadStatus && (
                  <div>
                    <p className={LABEL_CLS}>Status</p>
                    <p className="text-small text-midnight mt-0.5">
                      {LEAD_STATUS_LABEL[m.leadStatus as keyof typeof LEAD_STATUS_LABEL] ?? m.leadStatus}
                    </p>
                  </div>
                )}
                <div>
                  <p className={LABEL_CLS}>Created</p>
                  <p className="text-small text-midnight mt-0.5">{formatCreatedDate(m.createdAt)}</p>
                </div>
              </div>

              {/* Matched on */}
              <p className="text-[11px] text-slate">
                Matched on: {m.matchedOn.join(', ')}
              </p>

              <button
                type="button"
                onClick={() => onOpenLead(m.id)}
                className="h-10 rounded-lg bg-plum text-white text-[13px] font-semibold
                           hover:bg-rose transition-colors"
              >
                Open Existing Lead
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Submit Confirmation Dialog ──────────────────────────────────────────────

function SubmitDuplicateConfirm({ matches, onCreateAnyway, onOpenExisting, saving }: {
  matches: DuplicateMatch[];
  onCreateAnyway: () => void;
  onOpenExisting: (matches: DuplicateMatch[]) => void;
  saving: boolean;
}) {
  const displayName = matches[0]?.pointOfContact?.name || matches[0]?.customerCompanyName || 'Unknown';
  const createdStr = formatCreatedDate(matches[0]?.createdAt ?? '');
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-midnight/50" aria-hidden="true" />
      <div className="relative bg-white rounded-2xl p-5 max-w-sm w-full shadow-xl flex flex-col gap-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
            <AlertCircle size={20} className="text-amber-600" />
          </div>
          <div>
            <h3 className="font-display text-[16px] font-bold text-midnight">Possible duplicate</h3>
            <p className="text-small text-slate mt-1">
              This looks like an existing lead &mdash; {displayName}
              {createdStr ? `, ${createdStr}` : ''}.
              Create anyway, or open the existing record?
            </p>
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <button
            type="button"
            disabled={saving}
            onClick={onCreateAnyway}
            className="h-11 rounded-xl bg-plum text-white text-[14px] font-semibold
                       flex items-center justify-center gap-2
                       hover:bg-rose disabled:opacity-50 transition-colors"
          >
            {saving ? <LoadingSpinner size="sm" /> : null}
            {saving ? 'Creating...' : 'Create Anyway'}
          </button>
          <button
            type="button"
            onClick={() => onOpenExisting(matches)}
            className="h-11 rounded-xl border border-stone text-midnight text-[14px] font-semibold
                       hover:bg-mist transition-colors"
          >
            Open Existing
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Component ────────────────────────────────────────────────────────────────

export function NewMoveForm({ onClose, onCreate, showTypeSelector, defaultType, orgId }: NewMoveFormProps) {
  const { activeOrg, adminOrg } = useUserProfile();
  const navigate = useNavigate();
  const leadPipelineEnabled = activeOrg?.featureLeadPipeline ?? false;

  // Missing crew rates / packing item prices: warn once, before anything is typed.
  const possibleTypes = useMemo<QuoteType[]>(
    () => (showTypeSelector ? ['commercial', 'residential'] : [defaultType ?? 'commercial']),
    [showTypeSelector, defaultType],
  );
  const { gaps: pricingGaps } = useOrgPricingGaps(orgId ?? activeOrg?.orgId ?? '', possibleTypes);
  const [pricingWarningDismissed, setPricingWarningDismissed] = useState(false);
  const showPricingWarning = !pricingWarningDismissed && !!pricingGaps
    && (pricingGaps.crewRates || pricingGaps.materialPrices);

  // Customer fields
  const [customerName, setCustomerName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [quoteType, setQuoteType] = useState<QuoteType>(defaultType ?? 'commercial');

  // Move Info fields
  const [preferredDate, setPreferredDate] = useState('');
  const [estimatedDateRange, setEstimatedDateRange] = useState('');
  const [estimateDate, setEstimateDate] = useState('');
  const [bedrooms, setBedrooms] = useState('');
  const [employeeCount, setEmployeeCount] = useState('');
  const [sqft, setSqft] = useState('');
  const [originZip, setOriginZip] = useState('');
  const [destZip, setDestZip] = useState('');
  const [originPlace, setOriginPlace] = useState<SelectedPlace | null>(null);
  const [destPlace, setDestPlace] = useState<SelectedPlace | null>(null);
  const [showOriginAddress, setShowOriginAddress] = useState(false);
  const [showDestAddress, setShowDestAddress] = useState(false);
  const [moveType, setMoveType] = useState<'local' | 'intrastate' | 'interstate' | ''>('');

  // Lead Info fields
  const [discoverySource, setDiscoverySource] = useState<DiscoverySource | ''>('');
  const [inboundChannel, setInboundChannel] = useState<InboundChannel | ''>('');

  // Notes
  const [notes, setNotes] = useState('');

  // Follow-up
  const [nextFollowUp, setNextFollowUp] = useState(getDefaultFollowUp);
  const followUpInputRef = useRef<HTMLInputElement>(null);

  // UI state
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Duplicate checking
  const { checkDuplicates } = useDuplicateLeadCheck(orgId ?? null);
  const [viewingMatches, setViewingMatches] = useState<DuplicateMatch[] | null>(null);
  const [submitConfirmMatches, setSubmitConfirmMatches] = useState<DuplicateMatch[] | null>(null);
  const nameDebounceRef = useRef<ReturnType<typeof setTimeout>>();
  // Track which fields triggered the current inline banner
  const [phoneBannerMatches, setPhoneBannerMatches] = useState<DuplicateMatch[]>([]);
  const [emailBannerMatches, setEmailBannerMatches] = useState<DuplicateMatch[]>([]);
  const [nameBannerMatches, setNameBannerMatches] = useState<DuplicateMatch[]>([]);

  // Transit calculation state
  const [transitDistance, setTransitDistance] = useState<number | null>(null);
  const [transitDuration, setTransitDuration] = useState<number | null>(null);
  const [transitLoading, setTransitLoading] = useState(false);
  const transitAbortRef = useRef<AbortController>();

  const isCommercial = quoteType === 'commercial';

  // ── Auto-calculate transit + move type whenever addresses/zips change ────
  useEffect(() => {
    // Need at least one identifier for each side
    const hasOrigin = originPlace?.placeId || (originZip.trim().length === 5);
    const hasDest = destPlace?.placeId || (destZip.trim().length === 5);

    if (!hasOrigin || !hasDest || !orgId) {
      setTransitDistance(null);
      setTransitDuration(null);
      return;
    }

    // Debounce zip-only changes to avoid calling on every keystroke
    const timer = setTimeout(async () => {
      // Abort any in-flight request
      if (transitAbortRef.current) transitAbortRef.current.abort();
      const controller = new AbortController();
      transitAbortRef.current = controller;

      setTransitLoading(true);
      try {
        const res = await api.post<{
          success: boolean;
          data: { distanceMeters: number; durationSeconds: number; moveType: 'local' | 'intrastate' | 'interstate' };
        }>(`/quotes/${orgId}/calculate-lead-transit`, {
          originPlaceId: originPlace?.placeId || undefined,
          originZipCode: originZip.trim() || undefined,
          originAddress: originPlace?.address || undefined,
          destinationPlaceId: destPlace?.placeId || undefined,
          destinationZipCode: destZip.trim() || undefined,
          destinationAddress: destPlace?.address || undefined,
        });

        if (controller.signal.aborted) return;
        setTransitDistance(res.data.distanceMeters);
        setTransitDuration(res.data.durationSeconds);
        setMoveType(res.data.moveType);
      } catch {
        if (controller.signal.aborted) return;
        // Non-critical — leave transit display empty
        setTransitDistance(null);
        setTransitDuration(null);
      } finally {
        if (!controller.signal.aborted) setTransitLoading(false);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [originPlace?.placeId, originPlace?.address, originZip, destPlace?.placeId, destPlace?.address, destZip, orgId]);

  // ── Handle origin address change ─────────────────────────────────────────
  const handleOriginChange = useCallback((place: SelectedPlace | null) => {
    setOriginPlace(place);
    if (place) {
      const zipMatch = place.address.match(/\b(\d{5})\b/);
      if (zipMatch) setOriginZip(zipMatch[1]);
    }
  }, []);

  const handleDestChange = useCallback((place: SelectedPlace | null) => {
    setDestPlace(place);
    if (place) {
      const zipMatch = place.address.match(/\b(\d{5})\b/);
      if (zipMatch) setDestZip(zipMatch[1]);
    }
  }, []);

  // ── Duplicate check: phone on blur ────────────────────────────────────────
  const handlePhoneBlur = useCallback(async () => {
    if (!phone.trim()) { setPhoneBannerMatches([]); return; }
    const found = await checkDuplicates({ phone });
    setPhoneBannerMatches(found ?? []);
  }, [phone, checkDuplicates]);

  // ── Duplicate check: email on blur ────────────────────────────────────────
  const handleEmailBlur = useCallback(async () => {
    if (!email.trim()) { setEmailBannerMatches([]); return; }
    const found = await checkDuplicates({ email });
    setEmailBannerMatches(found ?? []);
  }, [email, checkDuplicates]);

  // ── Duplicate check: name debounced 600ms ─────────────────────────────────
  useEffect(() => {
    if (nameDebounceRef.current) clearTimeout(nameDebounceRef.current);
    if (!customerName.trim() || customerName.trim().length < 2) {
      setNameBannerMatches([]);
      return;
    }
    nameDebounceRef.current = setTimeout(async () => {
      const found = await checkDuplicates({ name: customerName });
      setNameBannerMatches(found ?? []);
    }, 600);
    return () => { if (nameDebounceRef.current) clearTimeout(nameDebounceRef.current); };
  }, [customerName, checkDuplicates]);

  // ── Combine inline matches for the banner (deduped) ───────────────────────
  const inlineMatches = (() => {
    const map = new Map<string, DuplicateMatch>();
    for (const m of [...phoneBannerMatches, ...emailBannerMatches, ...nameBannerMatches]) {
      if (!map.has(m.id)) map.set(m.id, m);
    }
    return Array.from(map.values());
  })();

  // ── View matches handler ──────────────────────────────────────────────────
  const handleViewMatches = useCallback((m: DuplicateMatch[]) => {
    setViewingMatches(m);
  }, []);

  // ── Open existing lead (navigate in new tab to not lose form state) ───────
  const handleOpenLead = useCallback((id: string) => {
    window.open(`/moves/${id}`, '_blank');
  }, []);

  // ── Build submission data ─────────────────────────────────────────────────
  const buildSubmitData = useCallback((): NewMoveFormData | null => {
    const hasName = customerName.trim();
    const hasCompany = companyName.trim();
    if (isCommercial && !hasName && !hasCompany) {
      setError('Either customer name or company name is required.');
      return null;
    }
    if (!isCommercial && !hasName) {
      setError('Customer name is required.');
      return null;
    }
    const displayName = hasName ? customerName.trim() : companyName.trim();

    // Determine lifecycleStage:
    // - Lead pipeline disabled → always 'quote' (skip lead stage entirely)
    // - Lead pipeline enabled + valid estimate date → 'quote' (skip lead stage)
    // - Lead pipeline enabled + no estimate date → 'lead'
    const hasValidEstimateDate = estimateDate.trim().length > 0;
    const lifecycleStage: 'lead' | 'quote' = !leadPipelineEnabled || hasValidEstimateDate ? 'quote' : 'lead';

    return {
      customerCompanyName: isCommercial && hasCompany ? companyName.trim() : displayName,
      pointOfContact: {
        name: hasName ? customerName.trim() : undefined,
        ...(phone.trim() ? { phone: phone.trim() } : {}),
        ...(email.trim() ? { email: email.trim() } : {}),
      },
      // Lead Info fields — only included when the pipeline is enabled
      ...(leadPipelineEnabled && discoverySource ? { discoverySource } : {}),
      ...(leadPipelineEnabled && inboundChannel ? { inboundChannel } : {}),
      ...(leadPipelineEnabled && nextFollowUp && lifecycleStage === 'lead' ? { nextFollowUp } : {}),
      type: quoteType,
      ...(preferredDate ? { movingDate: { preferredDate } } : {}),
      ...(estimatedDateRange.trim() ? { estimatedMoveDateRange: estimatedDateRange.trim() } : {}),
      ...(hasValidEstimateDate ? { estimateDate: estimateDate.trim() } : {}),
      ...(originZip.trim() ? { originZipCode: originZip.trim() } : {}),
      ...(destZip.trim() ? { destinationZipCode: destZip.trim() } : {}),
      ...(bedrooms ? { numberOfBedrooms: parseInt(bedrooms, 10) } : {}),
      ...(sqft.trim() ? { squareFootage: parseInt(sqft, 10) } : {}),
      ...(employeeCount.trim() ? { numberOfEmployees: parseInt(employeeCount, 10) } : {}),
      ...(moveType ? { moveType } : {}),
      ...(notes.trim() ? { notes: notes.trim() } : {}),
      lifecycleStage,
    };
  }, [customerName, companyName, phone, email, isCommercial, discoverySource, inboundChannel,
      quoteType, nextFollowUp, preferredDate, estimatedDateRange, estimateDate, originZip, destZip,
      bedrooms, sqft, employeeCount, moveType, notes, leadPipelineEnabled]);

  // ── Actually create the record ────────────────────────────────────────────
  const doCreate = useCallback(async () => {
    const data = buildSubmitData();
    if (!data) return;
    setError('');
    setSaving(true);
    try {
      await onCreate(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create move.');
      setSaving(false);
    }
  }, [buildSubmitData, onCreate]);

  // ── Submit ────────────────────────────────────────────────────────────────
  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const data = buildSubmitData();
    if (!data) return;

    // Final duplicate check on submit as safety net
    if (orgId) {
      setError('');
      setSaving(true);
      try {
        const submitMatches = await checkDuplicates({
          phone: phone.trim() || undefined,
          email: email.trim() || undefined,
          name: customerName.trim() || undefined,
        });
        if (submitMatches && submitMatches.length > 0) {
          // Show blocking confirmation
          setSaving(false);
          setSubmitConfirmMatches(submitMatches);
          return;
        }
      } catch {
        // If check fails, proceed with creation
      }
    }

    // No duplicates — create directly
    setSaving(false);
    await doCreate();
  }

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end md:items-center md:justify-center">
      <div className="absolute inset-0 bg-midnight/50" onClick={onClose} aria-hidden="true" />
      {showPricingWarning && pricingGaps && (
        <MissingPricingModal
          gaps={pricingGaps}
          isAdmin={!!adminOrg}
          onSetUp={() => {
            onClose();
            // Crew rates first (the wizard flows on into Materials Pricing); only prices missing → straight there.
            navigate(`/setup?step=${pricingGaps.crewRates ? 'rates' : 'materials-pricing'}`);
          }}
          onContinue={() => setPricingWarningDismissed(true)}
          onCancel={onClose}
        />
      )}
      <div className="relative bg-white rounded-t-2xl px-4 pt-5 pb-10 max-h-[85vh] overflow-y-auto
                      md:rounded-2xl md:max-w-lg md:w-full md:max-h-[85vh] md:pb-6 lg:max-w-2xl">
        <div className="md:hidden w-10 h-1 bg-stone rounded-full mx-auto mb-5" />

        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display text-h3 text-midnight">
            {leadPipelineEnabled ? 'New Move' : 'New Quote'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-slate
                       hover:text-midnight hover:bg-mist transition-colors"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-5">
          {error && (
            <div className="flex items-start gap-2 px-4 py-3 rounded-lg bg-rose/10 border border-rose/30">
              <AlertCircle size={15} className="shrink-0 text-rose mt-0.5" />
              <p className="text-small text-rose">{error}</p>
            </div>
          )}

          {/* Type selector */}
          {showTypeSelector && (
            <div className="flex flex-col gap-1.5">
              <label className={LABEL_CLS}>Type</label>
              <div className="flex bg-mist rounded-lg p-1 gap-1">
                {(['commercial', 'residential'] as const).map(t => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setQuoteType(t)}
                    className={`flex-1 py-2.5 text-sm font-semibold rounded-md transition-all duration-150
                      ${quoteType === t
                        ? 'bg-white text-midnight shadow-brand-sm'
                        : 'text-slate hover:text-midnight'
                      }`}
                  >
                    {t === 'commercial' ? 'Commercial' : 'Residential'}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ─── Customer Section ─────────────────────────────────────────── */}
          <div className="flex flex-col gap-3">
            <p className="text-[11px] font-bold uppercase tracking-widest text-slate border-b border-stone pb-1.5">
              Customer
            </p>

            <div className="flex flex-col gap-3">
              {/* Customer Name */}
              <div className="flex flex-col gap-1.5">
                <label htmlFor="nl-name" className={LABEL_CLS}>
                  Customer Name
                  {isCommercial
                    ? !companyName.trim() && <span className="text-rose ml-0.5">*</span>
                    : <span className="text-rose ml-0.5">*</span>
                  }
                </label>
                <input
                  id="nl-name"
                  type="text"
                  autoFocus={!isCommercial}
                  autoComplete="name"
                  value={customerName}
                  onChange={e => setCustomerName(e.target.value)}
                  placeholder="e.g. John Smith"
                  className={INPUT_CLS}
                />
                {/* Name duplicate banner */}
                {nameBannerMatches.length > 0 && phoneBannerMatches.length === 0 && emailBannerMatches.length === 0 && (
                  <DuplicateBanner matches={nameBannerMatches} onView={handleViewMatches} />
                )}
              </div>

              {/* Company Name — commercial only */}
              {isCommercial && (
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="nl-company" className={LABEL_CLS}>
                    Company Name{!customerName.trim() && <span className="text-rose ml-0.5">*</span>}
                  </label>
                  <input
                    id="nl-company"
                    type="text"
                    autoFocus
                    autoComplete="organization"
                    value={companyName}
                    onChange={e => setCompanyName(e.target.value)}
                    placeholder="e.g. Acme Corp"
                    className={INPUT_CLS}
                  />
                </div>
              )}

              {/* Phone + Email — side by side on larger screens */}
              <div className="flex flex-col gap-3 md:flex-row md:gap-3">
                <div className="flex flex-col gap-1.5 md:flex-1">
                  <label htmlFor="nl-phone" className={LABEL_CLS}>Phone Number</label>
                  <input
                    id="nl-phone"
                    type="tel"
                    autoComplete="tel"
                    value={phone}
                    onChange={e => setPhone(e.target.value)}
                    onBlur={handlePhoneBlur}
                    placeholder="e.g. (555) 123-4567"
                    className={INPUT_CLS}
                  />
                  {/* Phone duplicate banner */}
                  {phoneBannerMatches.length > 0 && (
                    <DuplicateBanner matches={phoneBannerMatches} onView={handleViewMatches} />
                  )}
                </div>
                <div className="flex flex-col gap-1.5 md:flex-1">
                  <label htmlFor="nl-email" className={LABEL_CLS}>Email</label>
                  <input
                    id="nl-email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    onBlur={handleEmailBlur}
                    placeholder="e.g. john@example.com"
                    className={INPUT_CLS}
                  />
                  {/* Email duplicate banner */}
                  {emailBannerMatches.length > 0 && phoneBannerMatches.length === 0 && (
                    <DuplicateBanner matches={emailBannerMatches} onView={handleViewMatches} />
                  )}
                </div>
              </div>

              {/* Combined inline banner when multiple triggers match */}
              {inlineMatches.length > 0 && phoneBannerMatches.length === 0 && emailBannerMatches.length === 0 && nameBannerMatches.length === 0 && (
                <DuplicateBanner matches={inlineMatches} onView={handleViewMatches} />
              )}
            </div>
          </div>

          {/* ─── Move Info Section ────────────────────────────────────────── */}
          <div className="flex flex-col gap-3">
            <p className="text-[11px] font-bold uppercase tracking-widest text-slate border-b border-stone pb-1.5">
              Move Info
            </p>

            <div className="flex flex-col gap-3">
              {/* Preferred Move Date + Estimated Date Range */}
              <div className="flex flex-col gap-3 md:flex-row md:gap-3">
                <div className="flex flex-col gap-1.5 md:flex-1">
                  <label className={LABEL_CLS}>Preferred Move Date</label>
                  <DateTimePicker value={preferredDate} onChange={setPreferredDate} placeholder="Set move date" />
                </div>
                <div className="flex flex-col gap-1.5 md:flex-1">
                  <label htmlFor="nl-date-range" className={LABEL_CLS}>Estimated Move Date Range</label>
                  <input
                    id="nl-date-range"
                    type="text"
                    value={estimatedDateRange}
                    onChange={e => setEstimatedDateRange(e.target.value)}
                    placeholder='e.g. "June" or "last week of October"'
                    className={INPUT_CLS}
                  />
                </div>
              </div>

              {/* Bedrooms (residential) or Employee Count (commercial) + Square Footage */}
              <div className="flex flex-col gap-3 md:flex-row md:gap-3">
                {isCommercial ? (
                  <div className="flex flex-col gap-1.5 md:flex-1">
                    <label htmlFor="nl-employees" className={LABEL_CLS}>Employee Count</label>
                    <input
                      id="nl-employees"
                      type="number"
                      inputMode="numeric"
                      value={employeeCount}
                      onChange={e => setEmployeeCount(e.target.value)}
                      placeholder="e.g. 50"
                      className={INPUT_CLS}
                    />
                  </div>
                ) : (
                  <div className="flex flex-col gap-1.5 md:flex-1">
                    <label htmlFor="nl-bedrooms" className={LABEL_CLS}>Home Size</label>
                    <CustomDropdown
                      id="nl-bedrooms"
                      options={BEDROOM_OPTIONS}
                      value={bedrooms}
                      onChange={v => setBedrooms(v)}
                      placeholder="Select..."
                    />
                  </div>
                )}
                <div className="flex flex-col gap-1.5 md:flex-1">
                  <label htmlFor="nl-sqft" className={LABEL_CLS}>Square Footage</label>
                  <input
                    id="nl-sqft"
                    type="number"
                    inputMode="numeric"
                    value={sqft}
                    onChange={e => setSqft(e.target.value)}
                    placeholder="e.g. 2000"
                    className={INPUT_CLS}
                  />
                </div>
              </div>

              {/* Origin — zip code with toggle to full address */}
              <div className="flex flex-col gap-1.5">
                <div className="flex items-center justify-between">
                  <label htmlFor="nl-origin-zip" className={LABEL_CLS}>
                    {showOriginAddress ? 'Origin Address' : 'Origin Zip Code'}
                  </label>
                  <button
                    type="button"
                    onClick={() => { setShowOriginAddress(v => !v); }}
                    className="text-[10px] font-semibold text-plum hover:text-rose transition-colors"
                  >
                    {showOriginAddress ? 'Use zip code' : 'Use full address'}
                  </button>
                </div>
                {showOriginAddress ? (
                  <AddressSearch
                    id="nl-origin-address"
                    value={originPlace}
                    onChange={handleOriginChange}
                  />
                ) : (
                  <input
                    id="nl-origin-zip"
                    type="text"
                    inputMode="numeric"
                    value={originZip}
                    onChange={e => setOriginZip(e.target.value)}
                    placeholder="e.g. 10001"
                    maxLength={5}
                    className={INPUT_CLS}
                  />
                )}
              </div>

              {/* Destination — zip code with toggle to full address */}
              <div className="flex flex-col gap-1.5">
                <div className="flex items-center justify-between">
                  <label htmlFor="nl-dest-zip" className={LABEL_CLS}>
                    {showDestAddress ? 'Destination Address' : 'Destination Zip Code'}
                  </label>
                  <button
                    type="button"
                    onClick={() => { setShowDestAddress(v => !v); }}
                    className="text-[10px] font-semibold text-plum hover:text-rose transition-colors"
                  >
                    {showDestAddress ? 'Use zip code' : 'Use full address'}
                  </button>
                </div>
                {showDestAddress ? (
                  <AddressSearch
                    id="nl-dest-address"
                    value={destPlace}
                    onChange={handleDestChange}
                  />
                ) : (
                  <input
                    id="nl-dest-zip"
                    type="text"
                    inputMode="numeric"
                    value={destZip}
                    onChange={e => setDestZip(e.target.value)}
                    placeholder="e.g. 90210"
                    maxLength={5}
                    className={INPUT_CLS}
                  />
                )}
              </div>

              {/* Distance & Transit Time */}
              {(transitDistance !== null || transitLoading) && (
                <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-mist border border-stone/50">
                  <MapPin size={14} className="shrink-0 text-plum" />
                  {transitLoading ? (
                    <span className="text-[13px] text-slate italic">Calculating distance...</span>
                  ) : (
                    <span className="text-[13px] text-midnight font-medium">
                      {formatDistance(transitDistance!)} &middot; {formatDuration(transitDuration!)}
                    </span>
                  )}
                </div>
              )}

              {/* Move Type */}
              <div className="flex flex-col gap-1.5">
                <label className={LABEL_CLS}>Move Type</label>
                <CustomDropdown
                  id="nl-move-type"
                  options={MOVE_TYPE_OPTIONS}
                  value={moveType}
                  onChange={v => setMoveType(v as 'local' | 'intrastate' | 'interstate' | '')}
                  placeholder="Select..."
                />
              </div>

              {/* Estimate Date + Time */}
              <div className="flex flex-col gap-1.5">
                <label className={LABEL_CLS}>
                  Estimate Date &amp; Time
                  <span className="text-slate font-normal normal-case tracking-normal ml-1">(optional)</span>
                </label>
                <DateTimePicker
                  value={estimateDate}
                  onChange={setEstimateDate}
                  placeholder="Set estimate appointment"
                />
                {leadPipelineEnabled && estimateDate && (
                  <p className="text-[11px] text-slate">
                    Will be created as a quote (skipping lead stage)
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* ─── Lead Info Section (only when lead pipeline is enabled) ───── */}
          {leadPipelineEnabled && (
          <div className="flex flex-col gap-3">
            <p className="text-[11px] font-bold uppercase tracking-widest text-slate border-b border-stone pb-1.5">
              Lead Info
            </p>

            <div className="flex flex-col gap-3">
              {/* How They Found Us */}
              <div className="flex flex-col gap-1.5">
                <label className={LABEL_CLS}>How Did You Hear About Us</label>
                <CustomDropdown
                  id="nl-discovery"
                  options={DISCOVERY_SOURCE_OPTIONS}
                  value={discoverySource}
                  onChange={v => setDiscoverySource(v as DiscoverySource | '')}
                  placeholder="Select a source..."
                />
              </div>

              {/* Inbound Channel */}
              <div className="flex flex-col gap-1.5">
                <label className={LABEL_CLS}>Inbound Channel</label>
                <CustomDropdown
                  id="nl-channel"
                  options={INBOUND_CHANNEL_OPTIONS}
                  value={inboundChannel}
                  onChange={v => setInboundChannel(v as InboundChannel | '')}
                  placeholder="Select a channel..."
                />
              </div>

              {/* Next Follow-Up */}
              <div className="flex flex-col gap-1">
                <label className={LABEL_CLS}>Next Follow-Up</label>
                <div className="relative h-11 rounded-lg border border-stone bg-white flex items-center px-3 gap-2
                                focus-within:border-plum focus-within:ring-2 focus-within:ring-plum/20
                                transition-colors duration-150">
                  <input
                    ref={followUpInputRef}
                    type="date"
                    value={nextFollowUp}
                    min={getTodayStr()}
                    onChange={e => setNextFollowUp(e.target.value)}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                  />
                  <span className={`flex-1 text-small ${nextFollowUp ? 'text-midnight' : 'text-slate italic'}`}>
                    {nextFollowUp ? formatDateDisplay(nextFollowUp) ?? 'Set date' : 'Set date'}
                  </span>
                  <Calendar size={16} className="text-slate shrink-0" />
                </div>
              </div>
            </div>
          </div>
          )}

          {/* ─── Notes Section ────────────────────────────────────────────── */}
          <div className="flex flex-col gap-3">
            <p className="text-[11px] font-bold uppercase tracking-widest text-slate border-b border-stone pb-1.5">
              Notes
            </p>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Add notes about this lead..."
              rows={3}
              className="w-full px-3 py-2.5 rounded-lg border border-stone bg-white
                         text-small text-midnight placeholder:text-slate
                         focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                         transition-colors duration-150 resize-none"
            />
          </div>

          {/* ─── Submit ───────────────────────────────────────────────────── */}
          <button
            type="submit"
            disabled={saving}
            className="h-12 rounded-xl bg-plum text-white text-[15px] font-semibold
                       flex items-center justify-center gap-2
                       hover:bg-rose active:opacity-80 disabled:opacity-50
                       transition-colors duration-150 mt-1"
          >
            {saving ? <LoadingSpinner size="sm" /> : <Plus size={18} />}
            {saving
              ? 'Creating...'
              : (!leadPipelineEnabled || estimateDate ? 'Create Quote' : 'Create Lead')}
          </button>
        </form>
      </div>

      {/* Existing lead comparison sheet */}
      {viewingMatches && (
        <ExistingLeadSheet
          matches={viewingMatches}
          onClose={() => setViewingMatches(null)}
          onOpenLead={handleOpenLead}
        />
      )}

      {/* Submit-time blocking confirmation */}
      {submitConfirmMatches && (
        <SubmitDuplicateConfirm
          matches={submitConfirmMatches}
          saving={saving}
          onCreateAnyway={() => {
            setSubmitConfirmMatches(null);
            doCreate();
          }}
          onOpenExisting={(m) => {
            setSubmitConfirmMatches(null);
            setViewingMatches(m);
          }}
        />
      )}
    </div>
  );
}
